/**
 * OTRACK-SUBSYSTEM — acquired-point "+" markers.
 *
 * AutoCAD marks an ACQUIRED object-snap tracking point with a small plus
 * sign next to the AutoSnap glyph. These markers are PERSISTENT for as long
 * as the point stays acquired — unlike the alignment-path guide lines
 * ({@link ./otrackGuide}), which only render while a path is actually
 * engaged near the cursor. A typed array of world points (never a single
 * point / boolean) — AutoCAD supports multiple simultaneously acquired
 * points (CLAUDE.md ПРИОРИТЕТ #1).
 */

import * as THREE from "three";

/** Generous cap on simultaneously-rendered acquisition markers. */
export const OTRACK_MARKER_POOL_SIZE = 16;

const OTRACK_MARKER_COLOR = "#3fa9ff"; // matches the alignment-path guide colour.

/** Plus-sign glyph, half the size of the OSNAP marker's own glyph (AutoCAD's acquisition mark is small). */
function buildPlusGlyph(): Float32Array {
  const h = 0.25;
  return new Float32Array([
    -h, 0, 0, h, 0, 0,
    0, -h, 0, 0, h, 0,
  ]);
}

export interface OtrackMarkerHandle {
  group: THREE.Group;
  /** Show a "+" at each world point (already in overlay/paper coords). */
  setPoints(points: ReadonlyArray<{ x: number; y: number }>, cameraZoom: number): void;
  setHidden(): void;
  dispose(): void;
}

export function createOtrackMarker(poolSize = OTRACK_MARKER_POOL_SIZE): OtrackMarkerHandle {
  const group = new THREE.Group();
  group.userData = { kind: "otrack-marker-pool" };
  group.renderOrder = 90;

  const material = new THREE.LineBasicMaterial({
    color: new THREE.Color(OTRACK_MARKER_COLOR),
    transparent: false,
    depthTest: false,
    depthWrite: false,
  });

  const glyph = buildPlusGlyph();
  const marks: THREE.LineSegments[] = [];
  for (let i = 0; i < poolSize; i++) {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(glyph.slice(), 3));
    const mesh = new THREE.LineSegments(geom, material);
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.renderOrder = 90;
    group.add(mesh);
    marks.push(mesh);
  }

  return {
    group,
    setPoints(points, cameraZoom) {
      const safeZoom = Math.max(cameraZoom, 1e-6);
      // Sized relative to the OSNAP marker's own PICKBOX-derived world size,
      // scaled down (the acquisition "+" reads smaller than the snap glyph
      // in real AutoCAD) — a fixed 12px-equivalent keeps it legible at any
      // zoom without depending on the OSNAP marker module.
      const worldSize = 12 / safeZoom;
      const n = Math.min(points.length, marks.length);
      for (let i = 0; i < n; i++) {
        const mesh = marks[i]!;
        const p = points[i]!;
        mesh.position.set(p.x, p.y, 0);
        mesh.scale.setScalar(worldSize);
        mesh.visible = true;
      }
      for (let i = n; i < marks.length; i++) {
        marks[i]!.visible = false;
      }
    },
    setHidden() {
      for (const mesh of marks) mesh.visible = false;
    },
    dispose() {
      for (const mesh of marks) mesh.geometry.dispose();
      material.dispose();
    },
  };
}
